import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../hooks/use-document-title';
import { MessageSquare, CheckCircle, XCircle, HelpCircle, Phone } from 'lucide-react';

export default function SMSGettingStarted() {
  useDocumentTitle('SMS updates');
  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />
      <main id="main-content" className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <article className="bg-white rounded-lg shadow-card border border-border p-6 sm:p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="h-10 w-10 text-gold shrink-0" aria-hidden="true" />
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-ink">SMS Service: Getting Started</h1>
          </div>
          
          <p className="text-gray-600 mb-8 text-lg">
            Stay connected with your custom invitation orders through our SMS service. Receive real-time updates, 
            order confirmations, design proofs, and more directly to your phone.
          </p>
          
          <div className="bg-gold/10 border-2 border-gold rounded-lg p-6 mb-8 text-center">
            <p className="text-sm text-gray-600 mb-2">Text us at:</p>
            <a href="sms:+18454425600" className="text-3xl font-bold text-gold hover:text-gold/80 transition-colors">
              845-442-5600
            </a>
            <p className="text-xs text-gray-500 mt-2">Tap to open your messaging app</p>
          </div>

          {/* Legal Disclosure */}
          <div className="bg-gray-900 text-white rounded-lg p-6 mb-8 border-2 border-gray-800">
            <p className="text-sm leading-relaxed text-center">
              <strong>Text START to 845-442-5600 to receive invitation updates.</strong> Message and data rates may apply. 
              Message frequency varies. Reply <strong>HELP</strong> for help, <strong>STOP</strong> to cancel. 
              View our <Link to="/privacy-policy" className="text-gold hover:text-gold/80 underline font-semibold">Privacy Policy</Link> and <Link to="/terms" className="text-gold hover:text-gold/80 underline font-semibold">Terms</Link>.
            </p>
          </div>

          <div className="max-w-none space-y-8">
            <section className="bg-blue/5 border-l-4 border-blue p-6 rounded-r-lg">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4 flex items-center gap-2">
                <Phone className="h-6 w-6 text-blue" />
                How to Get Started
              </h2>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue text-white rounded-full flex items-center justify-center font-bold">1</span>
                  <div>
                    <p className="font-semibold">Text START to our service number</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Send a text message with the word "START" to <strong className="text-ink font-semibold">845-442-5600</strong> to begin receiving SMS updates about your orders.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue text-white rounded-full flex items-center justify-center font-bold">2</span>
                  <div>
                    <p className="font-semibold">Confirm your subscription</p>
                    <p className="text-sm text-gray-600 mt-1">
                      You'll receive a confirmation message that you've successfully subscribed to our SMS service.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue text-white rounded-full flex items-center justify-center font-bold">3</span>
                  <div>
                    <p className="font-semibold">Receive order updates</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Once subscribed, you'll automatically receive transactional messages about your custom invitation orders, 
                      including confirmations, design proofs, and status updates.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">What You'll Receive</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-2">Order Confirmations</h3>
                  <p className="text-sm text-gray-600">Get notified when your order is successfully placed and confirmed.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-2">Design Proofs</h3>
                  <p className="text-sm text-gray-600">Receive links to review and approve your custom invitation designs.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-2">Status Updates</h3>
                  <p className="text-sm text-gray-600">Stay informed about your order's progress from creation to delivery.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-2">Account Notifications</h3>
                  <p className="text-sm text-gray-600">Important updates about your account and service-related information.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">SMS Commands</h2>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-gold" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">START</h3>
                      <p className="text-sm text-gray-600">
                        Begin receiving SMS messages. Text "START" to <strong className="text-ink">845-442-5600</strong> to subscribe to our SMS service and receive 
                        transactional updates about your orders.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                      <XCircle className="h-5 w-5 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">STOP</h3>
                      <p className="text-sm text-gray-600">
                        Unsubscribe from SMS messages. Text "STOP" to <strong className="text-ink">845-442-5600</strong> at any time to stop receiving messages. 
                        You'll receive a confirmation message, and you can re-subscribe anytime by texting "START" again.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue/10 rounded-lg flex items-center justify-center">
                      <HelpCircle className="h-5 w-5 text-blue" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">SUPPORT</h3>
                      <p className="text-sm text-gray-600">
                        Get assistance. Text "SUPPORT" to <strong className="text-ink">845-442-5600</strong>, then your question, to reach our team. 
                        Text "HELP" for our contact details. You can also contact us through our customer support system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">Important Information</h2>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 font-bold">•</span>
                  <p>
                    <strong>Message Frequency:</strong> Message frequency varies based on your order activity. 
                    You'll receive messages when you place orders, when design proofs are ready, and for important 
                    status updates.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 font-bold">•</span>
                  <p>
                    <strong>Message and Data Rates:</strong> Message and data rates may apply for messages sent to 
                    you from us and messages you send to us. Standard messaging rates from your wireless provider apply. 
                    If you have questions about your text plan or data plan, please contact your wireless provider.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 font-bold">•</span>
                  <p>
                    <strong>Carrier Liability:</strong> Carriers are not liable for delayed or undelivered messages. 
                    We are not responsible for messages that are not received due to carrier issues, network problems, 
                    or other technical difficulties beyond our control.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 font-bold">•</span>
                  <p>
                    <strong>Privacy:</strong> We respect your privacy. No mobile information will be shared with 
                    third parties or affiliates for marketing or promotional purposes. Your opt-in data and consent 
                    will not be shared with any third parties. For more information, please review our 
                    <Link to="/privacy-policy" className="text-blue-700 underline hover:text-blue-800 ml-1">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">Supported Carriers</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our SMS service works with all major wireless carriers in the United States, including but not limited to:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="text-gray-600">AT&T</div>
                <div className="text-gray-600">Verizon</div>
                <div className="text-gray-600">T-Mobile</div>
                <div className="text-gray-600">Sprint</div>
                <div className="text-gray-600">US Cellular</div>
                <div className="text-gray-600">Boost Mobile</div>
                <div className="text-gray-600">Cricket</div>
                <div className="text-gray-600">And more...</div>
              </div>
              <p className="text-gray-600 text-sm mt-4">
                If your carrier is not listed, the service may still work. Contact your carrier to confirm SMS 
                messaging capabilities.
              </p>
            </section>

            <section className="bg-gray-50 rounded-lg p-6">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">Need Help?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about our SMS service or need assistance, you can:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Text "SUPPORT" to <strong className="text-ink">845-442-5600</strong> to message our team</li>
                <li>Contact us through our customer support system</li>
                <li>Review our <Link to="/terms" className="text-blue-700 underline hover:text-blue-800">Terms and Conditions</Link> for more details</li>
                <li>Check our <Link to="/privacy-policy" className="text-blue-700 underline hover:text-blue-800">Privacy Policy</Link> to understand how we handle your information</li>
              </ul>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
